/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { transformLabelToAnnotation } from 'src/core/utils/labelingUtils';
import { LanguageCard } from 'src/languageStudio/appShell/prebuiltDemos/components/LanguageCard';
import { AssessmentLabelTooltip } from 'src/languageStudio/appShell/prebuiltDemos/sentimentDemo/AssessmentLabelTooltip';
import { SentenceCard } from 'src/languageStudio/appShell/prebuiltDemos/sentimentDemo/SentenceCard';
import { SentimentCard } from 'src/languageStudio/appShell/prebuiltDemos/sentimentDemo/SentimentCard';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { getLabel, getLabelId, getRelationAnnotations } from 'src/languageStudio/appShell/prebuiltDemos/utils/sentimentDemoUtils';
import {
    DetectedLanguagesDocuments,
    SentenceAssessment,
    SentenceTarget,
    SentimentDocuments
} from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import { Labeler } from 'src/packages/react-text-annotator/lib/esm';

export type ModifiedTarget = { sentenceIndex: number; targetIndex: number; target: SentenceTarget };
export type ModifiedAssessment = { sentenceIndex: number; assessmentIndex: number; assessment: SentenceAssessment };
export type VisualizedSentimentResultProps = {
    document: string;
    isLabelerRTL: boolean;
    isLabelerWordTokenized: boolean;
    isAssessmentMiningActive: boolean;
    sentimentResult: SentimentDocuments;
    detectedLanguage?: DetectedLanguagesDocuments;
};

const messages = defineMessages({
    analyzedSentiment: {
        id: 'languageStudio.visualizedSentimentResult.analyzedSentiment',
        defaultMessage: 'Analyzed sentiment'
    },
    target: {
        id: 'languageStudio.visualizedSentimentResult.target',
        defaultMessage: 'target'
    },
    assessment: {
        id: 'languageStudio.visualizedSentimentResult.assessment',
        defaultMessage: 'assessment'
    }
});

const theme = getScopedTheme('SentimentDemoPage');

export const VisualizedSentimentResult = (props: VisualizedSentimentResultProps) => {
    const { sentimentResult, document, isLabelerRTL, detectedLanguage, isAssessmentMiningActive, isLabelerWordTokenized } = props;

    const { localize } = useLocalization();

    const sentiment = sentimentResult.documents[0];
    let targets: ModifiedTarget[] = [];
    let assessments: ModifiedAssessment[] = [];

    if (isAssessmentMiningActive) {
        targets = sentiment.sentences.reduce(
            (acc, sentence, sentenceIndex) => [
                ...acc,
                ...(sentence.targets?.reduce(
                    (accumulate, target, targetIndex) => [...accumulate, { sentenceIndex, targetIndex, target }],
                    []
                ) || [])
            ],
            []
        );
        assessments = sentiment.sentences.reduce(
            (acc, sentence, sentenceIndex) => [
                ...acc,
                ...(sentence.assessments?.reduce(
                    (accumulate, assessment, assessmentIndex) => [...accumulate, { sentenceIndex, assessmentIndex, assessment }],
                    []
                ) || [])
            ],
            []
        );
    }

    const targetsTokens = targets.map(a => ({
        id: getLabelId(a.sentenceIndex, a.targetIndex, 'target'),
        tokens: { startTokenIndex: a.target.offset, endTokenIndex: a.target.offset + a.target.length - 1 }
    }));

    const assessmentsTokens = assessments.map(o => ({
        id: getLabelId(o.sentenceIndex, o.assessmentIndex, 'assessment'),
        tokens: { startTokenIndex: o.assessment.offset, endTokenIndex: o.assessment.offset + o.assessment.length - 1 }
    }));

    const annotations = React.useMemo(
        () =>
            [
                ...targets.map(t => getLabel(localize(messages.target), t.targetIndex, targetsTokens, t.sentenceIndex, 'target')),
                ...assessments.map(a =>
                    getLabel(localize(messages.assessment), a.assessmentIndex, assessmentsTokens, a.sentenceIndex, 'assessment')
                )
            ]
                .map(label =>
                    transformLabelToAnnotation(
                        label,
                        <AssessmentLabelTooltip label={label} assessments={assessments} targets={targets} />,
                        () => theme.labelColor
                    )
                )
                .concat(
                    getRelationAnnotations(
                        localize(messages.assessment),
                        theme.labelColor,
                        targets,
                        assessments,
                        targetsTokens,
                        assessmentsTokens
                    )
                ),
        [targets, assessments]
    );

    return (
        <Stack styles={{ root: { width: '100%' } }} tokens={{ childrenGap: 25 }}>
            <Stack tokens={{ childrenGap: 10 }}>
                <LuisText as="h3" fontWeight={500}>
                    {localize(messages.analyzedSentiment)}
                </LuisText>
                <SentimentCard sentimentType={sentiment.sentiment} isDocument confidenceDetails={sentiment.confidenceScores} />
            </Stack>

            <Stack data-automation-id="sentenceCardsContainer">
                {sentiment.sentences.map((sentence, sentenceIndex) => (
                    <SentenceCard
                        targets={targets}
                        key={sentenceIndex}
                        sentence={sentence}
                        assessments={assessments}
                        isLabelerRTL={isLabelerRTL}
                        targetsTokens={targetsTokens}
                        sentenceIndex={sentenceIndex}
                        assessmentsTokens={assessmentsTokens}
                        isLabelerWordTokenized={isLabelerWordTokenized}
                    />
                ))}
            </Stack>

            <LanguageCard language={detectedLanguage?.documents[0].detectedLanguage} />

            <Stack tokens={{ childrenGap: 10 }}>
                <LuisText as="h3" fontWeight={500}>
                    {localize(commonDemosMessages.originalText)}
                </LuisText>
                <Labeler
                    text={document}
                    annotations={annotations}
                    labelerConfigs={{
                        isDisabled: true,
                        isRtl: isLabelerRTL,
                        tokenizationType: isLabelerWordTokenized ? 'word' : 'character'
                    }}
                />
            </Stack>
        </Stack>
    );
};
